/**
 * CallOverlay - Full-screen video call UI with translation bubbles
 *
 * Mobile-first design inspired by Trulinco:
 * - Remote video fills screen
 * - Local video in small PiP (can hide)
 * - Tap to show/hide controls
 * - Real-time translation bubbles
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Video,
  VideoOff,
  SwitchCamera,
  Eye,
  EyeOff,
  MessageSquare,
  Globe,
  X
} from 'lucide-react'
import { useSpeechToText } from '../../hooks/useSpeechToText'

export default function CallOverlay({
  localStream,
  remoteStream,
  onHangup,
  onToggleMute,
  onToggleVideo,
  isMuted = false,
  isVideoOff = false,
  callerName = 'Friend',
  callerAvatar = null,
  callDuration = 0,
  // Translation options
  enableTranslation = false,
  myLanguage = 'en',
  theirLanguage = 'es',
}) {
  // UI state
  const [showControls, setShowControls] = useState(true)
  const [showSelf, setShowSelf] = useState(true)
  const [showTranslation, setShowTranslation] = useState(enableTranslation)
  const [messages, setMessages] = useState([])

  // Video refs
  const localVideoRef = useRef(null)
  const remoteVideoRef = useRef(null)
  const controlsTimeoutRef = useRef(null)

  // Speech-to-text for transcription
  const {
    start: startListening,
    stop: stopListening,
    transcript,
    isListening,
    clearTranscript
  } = useSpeechToText({ language: myLanguage })

  // Attach streams to video elements
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream
    }
  }, [localStream])

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream
    }
  }, [remoteStream])

  // Auto-hide controls after 3 seconds
  useEffect(() => {
    if (showControls) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false)
      }, 3000)
    }
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    }
  }, [showControls])

  // Handle tap to toggle controls
  const handleTap = useCallback((e) => {
    // Don't toggle if tapping on a button
    if (e.target.closest('button')) return
    setShowControls(prev => !prev)
  }, [])

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Flip camera (for mobile)
  const handleFlipCamera = async () => {
    if (!localStream) return
    const videoTrack = localStream.getVideoTracks()[0]
    if (!videoTrack) return

    try {
      const constraints = videoTrack.getConstraints()
      const currentFacing = constraints.facingMode || 'user'
      const newFacing = currentFacing === 'user' ? 'environment' : 'user'

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacing },
        audio: false
      })

      const newVideoTrack = newStream.getVideoTracks()[0]

      // Replace track in stream
      localStream.removeTrack(videoTrack)
      localStream.addTrack(newVideoTrack)
      videoTrack.stop()

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStream
      }
    } catch (err) {
      console.error('Failed to flip camera:', err)
    }
  }

  // Add message to translation bubbles
  const addMessage = useCallback((text, isMe = true, translated = null) => {
    const newMsg = {
      id: Date.now(),
      text,
      translated,
      isMe,
      timestamp: new Date()
    }
    setMessages(prev => [...prev.slice(-10), newMsg]) // Keep last 10 messages
  }, [])

  // When transcript updates, add to messages
  useEffect(() => {
    if (transcript && transcript.trim()) {
      // Could add translation here via API
      addMessage(transcript, true)
    }
  }, [transcript, addMessage])

  // Toggle speech recognition
  const toggleTranscription = () => {
    if (isListening) {
      stopListening()
    } else {
      startListening()
    }
    setShowTranslation(!showTranslation)
  }

  return (
    <div
      className="fixed inset-0 bg-black z-50 select-none"
      onClick={handleTap}
    >
      {/* Remote Video (Full Screen) */}
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Fallback when no remote video */}
      {!remoteStream && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-gray-900 to-black">
          {callerAvatar ? (
            <img
              src={callerAvatar}
              alt={callerName}
              className="w-32 h-32 rounded-full border-4 border-white/20 mb-4"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
              <span className="text-5xl font-bold text-white">
                {callerName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <h2 className="text-2xl font-semibold text-white mb-2">{callerName}</h2>
          <p className="text-gray-400">Connecting...</p>
        </div>
      )}

      {/* Local Video PiP (Your camera) */}
      {showSelf && localStream && (
        <div className="absolute top-4 left-4 w-28 h-40 sm:w-32 sm:h-44 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 bg-black">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover mirror"
            style={{ transform: 'scaleX(-1)' }}
          />
          {isVideoOff && (
            <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
              <VideoOff className="w-8 h-8 text-gray-500" />
            </div>
          )}
        </div>
      )}

      {/* Translation Bubbles */}
      {showTranslation && messages.length > 0 && (
        <div className="absolute right-2 bottom-32 left-2 sm:left-auto sm:right-4 sm:w-80 max-h-[40vh] overflow-y-auto flex flex-col gap-2 pointer-events-none">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`
                px-4 py-2.5 rounded-2xl max-w-full break-words
                ${msg.isMe
                  ? 'bg-orange-500 text-white ml-auto'
                  : 'bg-gray-800/90 text-white mr-auto'
                }
              `}
              style={{
                backdropFilter: 'blur(8px)',
                maxWidth: '90%'
              }}
            >
              <p className="text-sm sm:text-base leading-relaxed">{msg.text}</p>
              {msg.translated && (
                <p className="text-xs opacity-70 mt-1 border-t border-white/20 pt-1">
                  {msg.translated}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Top Bar - Call Info (always visible but fades) */}
      <div className={`
        absolute top-0 left-0 right-0 p-4 pt-safe
        bg-gradient-to-b from-black/60 to-transparent
        transition-opacity duration-300
        ${showControls ? 'opacity-100' : 'opacity-0'}
      `}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
              {callerAvatar ? (
                <img src={callerAvatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-semibold">
                  {callerName.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <h3 className="text-white font-medium">{callerName}</h3>
              <p className="text-white/60 text-sm">{formatDuration(callDuration)}</p>
            </div>
          </div>

          {/* Translation toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              toggleTranscription()
            }}
            className={`
              p-3 rounded-full transition-colors
              ${showTranslation
                ? 'bg-blue-500 text-white'
                : 'bg-white/20 text-white/70 hover:bg-white/30'
              }
            `}
          >
            <Globe className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className={`
        absolute bottom-0 left-0 right-0 pb-safe
        bg-gradient-to-t from-black/80 to-transparent
        transition-all duration-300
        ${showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
      `}>
        <div className="flex items-center justify-center gap-4 p-6">
          {/* Flip Camera */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleFlipCamera()
            }}
            className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <SwitchCamera className="w-5 h-5 text-white" />
          </button>

          {/* Toggle Self View */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowSelf(prev => !prev)
            }}
            className={`
              w-12 h-12 rounded-full flex items-center justify-center transition-colors
              ${showSelf
                ? 'bg-white/20 hover:bg-white/30'
                : 'bg-blue-500 hover:bg-blue-600'
              }
            `}
          >
            {showSelf ? (
              <Eye className="w-5 h-5 text-white" />
            ) : (
              <EyeOff className="w-5 h-5 text-white" />
            )}
          </button>

          {/* Mute */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleMute?.()
            }}
            className={`
              w-14 h-14 rounded-full flex items-center justify-center transition-colors
              ${isMuted
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-white/20 hover:bg-white/30'
              }
            `}
          >
            {isMuted ? (
              <MicOff className="w-6 h-6 text-white" />
            ) : (
              <Mic className="w-6 h-6 text-white" />
            )}
          </button>

          {/* Hang Up */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onHangup?.()
            }}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg shadow-red-500/30"
          >
            <PhoneOff className="w-7 h-7 text-white" />
          </button>

          {/* Video Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleVideo?.()
            }}
            className={`
              w-14 h-14 rounded-full flex items-center justify-center transition-colors
              ${isVideoOff
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-white/20 hover:bg-white/30'
              }
            `}
          >
            {isVideoOff ? (
              <VideoOff className="w-6 h-6 text-white" />
            ) : (
              <Video className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Hint text */}
        <p className="text-center text-white/40 text-xs pb-4">
          Tap anywhere to {showControls ? 'hide' : 'show'} controls
        </p>
      </div>
    </div>
  )
}
